import React, { useCallback, useEffect, useId, useState } from 'react';
import Button from 'react-bootstrap/Button';
import api from '../common/api';
import BuyInfoCenterModal from '../facility-number/BuyInfoCenterModal';
import { useFacilityAnisList, type FacilityAniEditable } from '../hooks/FacilityAni';
import { useAlerts } from '../alert/AlertContext';

const NAME_OPTIONS = ['Main', 'Fax', 'Inmate Voicemail'] as const;

type FacilityAnisSectionProps = {
  facility: { id: number };
  onBusyChange?: (busy: boolean) => void;
};

const FacilityAnisSection: React.FC<FacilityAnisSectionProps> = ({ facility, onBusyChange }) => {
  const nameListId = useId();
  const alerts = useAlerts();

  const {
    execute: refetch,
    result: listFromApi,
    loading,
    error: listError,
  } = useFacilityAnisList(facility.id);

  const [rows, setRows] = useState<FacilityAniEditable[]>([]);

  useEffect(() => {
    if (listFromApi != null) {
      setRows(listFromApi);
    }
  }, [listFromApi]);
  const [editing, setEditing] = useState<Set<number>>(() => new Set());
  const [saving, setSaving] = useState<Set<number>>(() => new Set());
  const [buyInfoOpen, setBuyInfoOpen] = useState(false);
  const [buyInfoBasis, setBuyInfoBasis] = useState<FacilityAniEditable | null>(null);

  useEffect(() => {
    if (listError != null) {
      alerts.error('An error occurred while loading ANIs');
    }
  }, [listError]);

  const aniBusy = loading || saving.size > 0;
  useEffect(() => {
    onBusyChange?.(aniBusy);
  }, [aniBusy, onBusyChange]);

  const addAni = useCallback(() => {
    const row: FacilityAniEditable = {
      id: undefined,
      ani: '',
      name: '',
      extension: '',
    };
    setRows((r) => [row, ...r]);
    setEditing((e) => new Set(e).add(row.id ?? 0));
  }, []);

  const startEdit = useCallback((row: FacilityAniEditable) => {
    setEditing((e) => new Set(e).add(row.id ?? 0));
  }, []);

  const setField = useCallback((k: number, field: 'ani' | 'name' | 'extension', value: string) => {
    setRows((prev) => prev.map((r) => (r.id === k ? { ...r, [field]: value } : r)));
  }, []);

  const saveRow = useCallback(
    (row: FacilityAniEditable) => {
      const k = row.id ?? 0;
      if (row.ani.trim() === '' || row.name.trim() === '') {
        alerts.error('ANI and Name are required');
        return;
      }
      const payload: Record<string, unknown> = {
        ani: row.ani.trim(),
        name: row.name.trim(),
        extension: row.extension.trim(),
        facilityId: facility.id,
      };
      if (row.id != null) {
        payload.id = row.id;
      }

      setSaving((s) => new Set(s).add(k));

      const done = () => {
        setSaving((s) => {
          const n = new Set(s);
          n.delete(k);
          return n;
        });
      };

      const p =
        row.id != null
          ? api.put(`/facility-ani/${row.id}`, payload)
          : api.post('/facility-ani', payload);

      void p.then(
        () => {
          alerts.success('The ANI was saved');
          setEditing((e) => {
            const n = new Set(e);
            n.delete(k);
            return n;
          });
          void refetch(facility.id);
          done();
        },
        () => {
          alerts.error('An error occurred while saving the ANI');
          done();
        }
      );
    },
    [facility.id, refetch]
  );

  const onBuyInfoCenter = useCallback(() => {
    const basis = rows.length > 0 ? rows[0] : null;
    setBuyInfoBasis(basis);
    setBuyInfoOpen(true);
  }, [rows]);

  const onBuyInfoModalClose = useCallback(
    (_saved: boolean) => {
      setBuyInfoOpen(false);
      setBuyInfoBasis(null);
      void refetch(facility.id);
    },
    [facility.id, refetch]
  );

  const hasInfoCenter = rows.some((r) => r.name === 'Info Center');

  return (
    <>
      <Button
        type="button"
        variant="primary"
        className="btn-labeled mb-2 me-2"
        disabled={loading}
        onClick={addAni}>
        <span className="btn-label">
          <i className="fas fa-plus-circle" />{' '}
        </span>
        Add Inbound Number
      </Button>
      {!hasInfoCenter && (
        <Button
          type="button"
          variant="primary"
          className="btn-labeled mb-2"
          disabled={loading}
          onClick={onBuyInfoCenter}>
          <span className="btn-label">
            <i className="fas fa-plus-circle" />{' '}
          </span>
          Buy Info Center Number
        </Button>
      )}
      {loading && rows.length === 0 && <p className="text-muted">Loading…</p>}
      {rows.length > 0 && (
        <table className="table table-striped table-hover table-bordered">
          <thead>
            <tr>
              <th>ANI</th>
              <th>Name</th>
              <th>Extension</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => {
              const k = row.id ?? 0;
              const edit = editing.has(k);
              const sav = saving.has(k);
              return (
                <tr key={k}>
                  <td>
                    <input
                      className="form-control"
                      value={row.ani}
                      disabled={!edit || sav}
                      required
                      onChange={(e) => setField(k, 'ani', e.target.value)}
                    />
                  </td>
                  <td>
                    <input
                      className="form-control"
                      type="text"
                      list={nameListId}
                      value={row.name}
                      disabled={!edit || sav}
                      required
                      onChange={(e) => setField(k, 'name', e.target.value)}
                    />
                  </td>
                  <td>
                    <input
                      className="form-control"
                      type="text"
                      value={row.extension}
                      disabled={!edit || sav}
                      onChange={(e) => setField(k, 'extension', e.target.value)}
                    />
                  </td>
                  <td>
                    {edit && (
                      <Button
                        type="button"
                        variant="secondary"
                        size="sm"
                        disabled={sav}
                        onClick={() => saveRow(row)}>
                        {sav ? 'Saving…' : 'Save'}
                      </Button>
                    )}
                    {!edit && (
                      <Button type="button" variant="secondary" size="sm" onClick={() => startEdit(row)}>
                        Edit
                      </Button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
      <datalist id={nameListId}>
        {NAME_OPTIONS.map((n) => (
          <option key={n} value={n} />
        ))}
      </datalist>
      {!loading && rows.length === 0 && <p className="text-muted">No inbound numbers.</p>}
      <BuyInfoCenterModal
        show={buyInfoOpen}
        facility={facility}
        basisNumber={buyInfoBasis}
        onClose={onBuyInfoModalClose}
      />
    </>
  );
};

export default FacilityAnisSection;
